/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import Lottie, { type LottieComponentProps } from 'lottie-react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import AtlaskitSkeleton from '@atlaskit/skeleton';

const styles = cssMap({
	container: {
		position: 'relative',
	},
	contentContainer: {
		transition: 'opacity 0.3s ease-in-out',
	},
	contentVisible: {
		opacity: 1,
	},
	contentHidden: {
		opacity: 0,
	},
	skeletonContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		transition: 'opacity 0.3s ease-in-out',
	},
	skeletonVisible: {
		opacity: 1,
	},
	skeletonHidden: {
		opacity: 0,
	},
});

export type SkeletonAnimationProps = {
	children: React.ReactNode;
	height?: string;
	isLoading: boolean;
	width?: string;
};

export const Skeleton = ({
	children,
	isLoading,
	width = '100%',
	height = '100%',
}: SkeletonAnimationProps): JSX.Element => {
	return (
		<Box xcss={styles.container}>
			<Box
				xcss={cx(
					styles.skeletonContainer,
					isLoading ? styles.skeletonVisible : styles.skeletonHidden,
				)}
			>
				<AtlaskitSkeleton height={height} width={width} isShimmering />
			</Box>

			{!isLoading ? (
				<Box xcss={cx(styles.contentContainer, styles.contentVisible)}>{children}</Box>
			) : null}
		</Box>
	);
};

export type SkeletonWithAnimationProps = {
	lottieProps: LottieComponentProps;
};

export const SkeletonWithAnimation = ({ lottieProps }: SkeletonWithAnimationProps): JSX.Element => {
	const isLoading = !lottieProps.animationData;

	return (
		<Skeleton isLoading={isLoading}>
			<Lottie {...lottieProps} />
		</Skeleton>
	);
};
